/**
 * PATH: apps/quote/src/pages/pricelist-page/usePricelistData.ts
 * PURPOSE: Data fetching hooks and mutations for PricelistPage
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quoteApi } from '@/lib/quoteApi';
import type { CreatePriceBookItemRequest } from '@/lib/quoteApi';

export type Tab = 'items' | 'workbook' | 'xero' | 'suppliers' | 'historical' | 'suggestions' | 'materials' | 'pivot';

export function usePricelistData(activeTab: Tab) {
  const queryClient = useQueryClient();

  // Fetch pricebooks
  const {
    data: pricebooks = [],
    isLoading: pricebooksLoading,
    isError: pricebooksError,
    error: pricebooksErrorData,
  } = useQuery({
    queryKey: ['pricebooks'],
    queryFn: () => quoteApi.listPricebooks(),
    retry: 1,
  });

  // Xero sync status
  const {
    data: xeroStatus,
    isError: xeroStatusError,
    error: xeroStatusErrorData,
  } = useQuery({
    queryKey: ['xero-sync-status'],
    queryFn: () => quoteApi.getXeroSyncStatus(),
    staleTime: 60000,
    retry: 1,
  });

  // Xero contacts status
  const {
    data: xeroContactsStatus,
    isError: xeroContactsError,
  } = useQuery({
    queryKey: ['xero-contacts-status'],
    queryFn: () => quoteApi.getXeroContactsStatus(),
    staleTime: 60000,
    enabled: activeTab === 'xero',
    retry: 1,
  });

  // Historical pricing analysis
  const {
    data: historicalData,
    isLoading: historicalLoading,
  } = useQuery({
    queryKey: ['historical-pricing'],
    queryFn: () => quoteApi.analyzeHistoricalPricing({ months_back: 12, min_occurrences: 2 }),
    staleTime: 300000,
    enabled: activeTab === 'historical',
  });

  // Supplier quotes
  const {
    data: supplierQuotes = [],
    isLoading: supplierQuotesLoading,
  } = useQuery({
    queryKey: ['supplier-quotes'],
    queryFn: () => quoteApi.listSupplierQuotes(),
    enabled: activeTab === 'suppliers',
  });

  // Xero items sync
  const xeroSyncMutation = useMutation({
    mutationFn: () => quoteApi.triggerXeroSync({ full_sync: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
      queryClient.invalidateQueries({ queryKey: ['xero-sync-status'] });
    },
  });

  // Xero contacts sync
  const xeroContactsSyncMutation = useMutation({
    mutationFn: () => quoteApi.triggerXeroContactsSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-contacts-status'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });

  // Generate pricebook from history
  const generatePricebookMutation = useMutation({
    mutationFn: () => quoteApi.generateHistoricalPricebook({
      name: `Historical Pricing ${new Date().toISOString().slice(0, 10)}`,
      months_back: 12,
      min_occurrences: 3,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] });
    },
  });

  // Delete item
  const deleteItemMutation = useMutation({
    mutationFn: ({ pricebookId, itemId }: { pricebookId: string; itemId: string }) =>
      quoteApi.deletePricebookItem(pricebookId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
    },
  });

  // Create item (Add Item)
  const createItemMutation = useMutation({
    mutationFn: ({ pricebookId, data }: { pricebookId: string; data: CreatePriceBookItemRequest }) =>
      quoteApi.createPricebookItem(pricebookId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
    },
  });

  // Update item (row Edit)
  const updateItemMutation = useMutation({
    mutationFn: ({ pricebookId, itemId, data }: { pricebookId: string; itemId: string; data: Partial<CreatePriceBookItemRequest> }) =>
      quoteApi.updatePricebookItem(pricebookId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
    },
  });

  // Apply an AI/historical price suggestion = set the item's sell price to the
  // suggested value (fixed). Dismiss is client-side (suggestions are recomputed
  // live from quotes, so there is no persistent dismiss record to write).
  const applySuggestionMutation = useMutation({
    mutationFn: ({ pricebookId, itemId, suggestedPrice }: { pricebookId: string; itemId: string; suggestedPrice: number }) =>
      quoteApi.updatePricebookItem(pricebookId, itemId, {
        pricing_method: 'fixed',
        fixed_sell_price: suggestedPrice,
      }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
      queryClient.invalidateQueries({ queryKey: ['historical-suggestions', vars.pricebookId] });
    },
  });

  return {
    queryClient,
    pricebooks,
    pricebooksLoading,
    pricebooksError,
    pricebooksErrorData,
    xeroStatus,
    xeroStatusError,
    xeroStatusErrorData,
    xeroContactsStatus,
    xeroContactsError,
    historicalData,
    historicalLoading,
    supplierQuotes,
    supplierQuotesLoading,
    xeroSyncMutation,
    xeroContactsSyncMutation,
    generatePricebookMutation,
    deleteItemMutation,
    createItemMutation,
    updateItemMutation,
    applySuggestionMutation,
  };
}

export function usePricebookItems(activePricebookId: string | undefined) {
  const {
    data: items = [],
    isLoading: itemsLoading,
  } = useQuery({
    queryKey: ['pricebook-items', activePricebookId],
    queryFn: () => activePricebookId ? quoteApi.listPricebookItems(activePricebookId) : [],
    enabled: !!activePricebookId,
  });

  return { items, itemsLoading };
}

export function useHistoricalSuggestions(activeTab: Tab, activePricebookId: string | undefined) {
  const {
    data: historicalSuggestions,
    isLoading: suggestionsLoading,
  } = useQuery({
    queryKey: ['historical-suggestions', activePricebookId],
    queryFn: () => quoteApi.getHistoricalSuggestions({ pricebook_id: activePricebookId }),
    staleTime: 300000,
    enabled: activeTab === 'suggestions' && !!activePricebookId,
  });

  return { historicalSuggestions, suggestionsLoading };
}

export function useFilteredItems(
  items: any[],
  searchQuery: string,
  categoryFilter: string
) {
  const categories = useMemo(() => {
    const types = [...new Set(items.map((item) => item.item_type))];
    return types.filter(Boolean);
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        item.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.item_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || item.item_type === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchQuery, categoryFilter]);

  return { categories, filteredItems };
}
